import { PostsQuery, PostsDocument } from '@/graphql/generated/posts.generated';
import { PostGroupBy, PostsViewType } from '@/graphql/generated/types.generated';
import { useQuery } from '@apollo/client';
import {
    createContext,
    PropsWithChildren,
    useContext,
    useState,
    useMemo,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePostsFilters } from '../filters/PostsFiltersProvider';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { IdeasDocument, IdeasQuery, IdeasQueryVariables } from '@/graphql/generated/ideas.generated';
import { FetchMoreFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import { useSession } from '@/providers/session-provider';
import { useAndie } from '@/providers/andie-provider';
import { usePostNavigation } from '@/hooks/use-post-navigation';
import { postIsIdea } from '../utils/post-is-idea.util';

// =============================================================================
// Types
// =============================================================================

/** Single post type derived from the PostsQuery */
export type Post = PostsQuery['posts']['items'][number];

/** Total info for a group (day or stage) */
export type PostsTotal = PostsQuery['posts']['totals'][number];

/** Budget info */
export type PostsBudget = PostsQuery['posts']['budgets'][number];

/** Group metadata with posts and pagination info */
export interface PostsGroup {
    /** Key for this group - either a date string (YYYY-MM-DD) or PostStage */
    key: string;
    /** Posts in this group */
    posts: Post[];
    /** Total number of posts in this group (from server) */
    total: number;
    /** Whether there are more posts to load in this group */
    hasMore: boolean;
    /** Whether this group is currently loading more posts */
    loading: boolean;
    /** Whether this group is expanded in the UI */
    expanded: boolean;
    /** Function to toggle expanded state */
    toggleExpand: (groupKey: string) => void;
}

/** Grouped posts structure - Map of group key to group data */
export type PostsGroupedMap = Map<string, PostsGroup>;

/** Posts view type */
export type PostsView = 'calendar' | 'list' | 'kanban';

export type IdeasState = IdeasQuery['ideas'];

// =============================================================================
// Context Value Interface
// =============================================================================

export interface PostsContextValue {
    // ---------------------------------------------------------------------------
    // State
    // ---------------------------------------------------------------------------

    /** All posts in a flat array */
    posts: Post[];

    /** Posts grouped by the current groupBy setting */
    postsGrouped: PostsGroupedMap;

    /** Current groupBy setting */
    groupBy: PostGroupBy;

    /** Totals from the server (for each day/stage) */
    totals: PostsTotal[];

    /** Budgets summary */
    budgets: PostsBudget[];

    /** Whether the posts query is loading */
    loadingPosts: boolean;

    /** Ideas state */
    ideasState: IdeasState;

    /** Whether the ideas query is loading */
    loadingIdeas: boolean;

    /** Posts query error if any */
    errorPosts: Error | null;

    /**
     * The timezone the days were grouped and counted in. Views must render times in it, otherwise a
     * post shows a time that belongs to a different day than the cell it sits in.
     */
    timezone?: string;

    /** Ideas query error if any */
    errorIdeas: Error | null;

    // ---------------------------------------------------------------------------
    // CRUD Operations
    // ---------------------------------------------------------------------------

    /**
     * Add a new post to the state.
     * This should be called after a successful create/duplicate mutation.
     * The post will be automatically placed in the correct group based on its publishAt/stage.
     */
    addPost: (post: Post) => void;

    /**
     * Update an existing post in the state.
     * This should be called after a successful update mutation.
     * If the post's publishAt or stage changed, it will be moved to the correct group.
     */
    updatePost: (post: Post) => void;

    /**
     * Remove a post from the state.
     * This should be called after a successful delete mutation.
     */
    removePost: (post: Post) => void;

    /**
     * Get a post by ID from the current state.
     * This should be called to retrieve a post without needing to query the server.
     */
    getPost: (postId: string) => Post | null;

    // ---------------------------------------------------------------------------
    // Pagination
    // ---------------------------------------------------------------------------

    /**
     * Load more posts for a specific group (day or stage).
     * Used by CalendarDay or ListView groups for infinite scroll.
     *
     * @param groupKey - The day (YYYY-MM-DD) or stage key
     * @returns Promise that resolves when fetch completes
     */
    fetchMoreForGroup: (groupKey: string) => Promise<void>;

    /**
     * Load more ideas for infinite scroll.
     * @returns Promise that resolves when fetch completes
     */
    fetchMoreIdeas: FetchMoreFunction<IdeasQuery, IdeasQueryVariables>;

    /**
     * Re-fetch the ideas from the server.
     * Ideas carry a workflow stage that teammates change elsewhere, and nothing in this session
     * signals that, so the user needs a way to ask for fresh data.
     */
    refetchIdeas: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const PostsContext = createContext<PostsContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

const PostsProviderContent = ({
    children,
    viewType,
    timezone,
}: PropsWithChildren<{ viewType: PostsViewType; timezone?: string }>) => {
    const { setHelper } = useAndie();
    const { navigateToPost, navigateBackFromPost } = usePostNavigation();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { filters: postViewFilters, loading: loadingPostViewFilters, loadingView } = usePostsFilters();
    // ---------------------------------------------------------------------------
    // Constants
    // ---------------------------------------------------------------------------

    const INITIAL_LOAD_CALENDAR = 3;
    const INITIAL_LOAD_LIST = 1;
    const INITIAL_LOAD_KANBAN = 10;
    const INITIAL_LOAD_POSTS =
        viewType === PostsViewType.Calendar
            ? INITIAL_LOAD_CALENDAR
            : viewType === PostsViewType.Kanban
              ? INITIAL_LOAD_KANBAN
              : INITIAL_LOAD_LIST;

    const LOAD_MORE_CALENDAR = 3;
    const LOAD_MORE_LIST = 20;
    const LOAD_MORE_POSTS = viewType === PostsViewType.Calendar ? LOAD_MORE_CALENDAR : LOAD_MORE_LIST;

    // ---------------------------------------------------------------------------
    // Query Variables from URL params and filters
    // ---------------------------------------------------------------------------

    const now = dayjs().tz(timezone);
    const month = parseInt(searchParams.get('month') || now.format('MM'));
    const year = parseInt(searchParams.get('year') || now.format('YYYY'));
    const currentMonth = dayjs.tz(`${year}-${String(month).padStart(2, '0')}-01`, timezone).startOf('month');

    const since = currentMonth.startOf('month').toDate();
    const until = currentMonth.endOf('month').toDate();

    // Load filters from context and process them into query variables
    const {
        search,
        stages,
        types,
        netProfilesAuth,
        assignedTo,
        labels,
        groupBy: groupByFilter,
    } = postViewFilters;
    const netProfileAuthIds = netProfilesAuth?.map((npa) => npa.id) ?? undefined;
    const assignedToIds = assignedTo?.map((a) => a.id) ?? undefined;
    const labelsIds = labels?.map((l) => l.id) ?? undefined;
    const groupBy = (() => {
        if (viewType === PostsViewType.Calendar) {
            return PostGroupBy.Date;
        } else if (viewType === PostsViewType.Kanban) {
            return PostGroupBy.Stage;
        }

        return groupByFilter || PostGroupBy.Date;
    })();

    // ---------------------------------------------------------------------------
    // Internal State
    // ---------------------------------------------------------------------------

    const [postsGroupedState, setPostsGroupedState] = useState<PostsGroupedMap>(new Map());
    const [ideasState, setIdeasState] = useState<IdeasState>({
        items: [],
        total: 0,
        hasMore: false,
    });

    const skipPostsQueryNotLoaded = loadingPostViewFilters || loadingView;

    // ---------------------------------------------------------------------------
    // GraphQL Query
    // ---------------------------------------------------------------------------

    const {
        loading: loadingPosts,
        error: errorPosts,
        data: postsData,
        fetchMore: fetchMorePosts,
    } = useQuery(PostsDocument, {
        variables: {
            assignedToIds,
            labelsIds,
            netProfileAuthIds,
            search,
            ...(stages && { stages }),
            types,
            ...(viewType !== PostsViewType.Kanban && { since, until }),
            groupBy,
            take: INITIAL_LOAD_POSTS,
        },
        skip: skipPostsQueryNotLoaded,
        fetchPolicy: 'network-only',
        onCompleted: ({ posts }) => {
            initializeGroupsFromResponse(posts);
        },
        onError: (error) => {
            enqueueSnackbar(error.message);
        },
    });

    /**
     * Ideas are posts without a date, whatever their stage. They only need a home of their own when
     * the posts are grouped by date, since a date is what places a post in the main view. Grouped by
     * stage, a dateless post already shows under its own stage.
     */
    const shouldFetchIdeas = groupBy === PostGroupBy.Date;

    const {
        loading: loadingIdeas,
        error: errorIdeas,
        fetchMore: fetchMoreIdeas,
        refetch: refetchIdeasQuery,
    } = useQuery(IdeasDocument, {
        skip: !shouldFetchIdeas || skipPostsQueryNotLoaded,
        variables: {
            assignedToIds,
            labelsIds,
            netProfileAuthIds,
            search,
            ...(stages && { stages }),
            types,
        },
        /**
         * Same policy as the posts query above. Ideas now carry a workflow stage that teammates move
         * from elsewhere, so serving them from cache would show a stale stage until a hard refresh.
         */
        fetchPolicy: 'network-only',
        onCompleted: ({ ideas }) => {
            setIdeasState(ideas);
        },
        onError: (error) => {
            enqueueSnackbar(error.message);
        },
    });

    // If we should not fetch ideas, ensure ideasState is empty
    if (!shouldFetchIdeas && ((ideasState.items ?? []).length > 0 || (ideasState.total ?? 0) > 0)) {
        setIdeasState({ items: [], total: 0, hasMore: false });
    }

    /**
     * Reads the result directly instead of relying on onCompleted, which Apollo does not guarantee
     * to fire on a refetch.
     */
    const refetchIdeas = useCallback(async () => {
        const { data } = await refetchIdeasQuery();

        if (data?.ideas) {
            setIdeasState(data.ideas);
        }
    }, [refetchIdeasQuery]);

    // ---------------------------------------------------------------------------
    // Computed State
    // ---------------------------------------------------------------------------

    const posts = useMemo(() => {
        const allPosts: Post[] = [];
        postsGroupedState.forEach((group) => {
            allPosts.push(...group.posts);
        });

        return allPosts;
    }, [postsGroupedState]);
    const totals = postsData?.posts.totals || [];
    const budgets = postsData?.posts.budgets || [];

    // ---------------------------------------------------------------------------
    // Helper Functions
    // ---------------------------------------------------------------------------

    /**
     * Toggle expanded state for a group
     */
    const toggleGroupExpand = useCallback((groupKey: string) => {
        setPostsGroupedState((prev) => {
            const newMap = new Map(prev);
            const group = newMap.get(groupKey);
            if (!group) return newMap;

            newMap.set(groupKey, { ...group, expanded: !group.expanded });

            return newMap;
        });
    }, []);

    /**
     * The timezone the server grouped and counted in. It travels with the payload because the shared
     * view has no session to read a workspace from, and grouping by anything else puts posts on a
     * different day than the totals they belong to. The prop only covers the first render.
     */
    const groupingTimezone = postsData?.posts.timezone ?? timezone;

    /**
     * Get the group key for a post based on current groupBy setting.
     *
     * Date grouping uses the day the server already resolved, so the client never re-derives it and
     * cannot land on a different day than the totals. The fallback covers a payload from a server
     * that predates the field.
     */
    const getGroupKey = useCallback(
        (post: Post): string => {
            if (groupBy === PostGroupBy.Stage) {
                return post.stage;
            }
            // Group by date
            if (post.day) {
                return post.day;
            }
            if (post.publishAt) {
                return dayjs(post.publishAt).tz(groupingTimezone).format('YYYY-MM-DD');
            }
            // Guarded by belongsToGroupedView: a dateless post never reaches a date group
            return post.stage;
        },
        [groupBy, groupingTimezone],
    );

    /**
     * A dateless post has no day to sit on, so it is served by the ideas sidebar instead of the
     * grouped view. Grouped by stage it has a home of its own and belongs in the view.
     *
     * The stage filter is checked for the same reason: a post whose stage moved outside the active
     * filter is no longer part of this list, and the next fetch would not return it either. On a
     * SHARED view that is worse than cosmetic — the view's stages also gate what the server will
     * open, so a client approving a post in a view filtered to earlier stages was left with a card
     * that stayed on the calendar and then refused to open.
     */
    const belongsToGroupedView = useCallback(
        (post: Post) => {
            if (groupBy === PostGroupBy.Date && !post.publishAt) {
                return false;
            }

            // Empty means "no stage filter", which every stage satisfies.
            return !stages?.length || stages.includes(post.stage);
        },
        [groupBy, stages],
    );

    /**
     * Initialize postsGroupedState from server response
     */
    const initializeGroupsFromResponse = useCallback(
        (response: PostsQuery['posts']) => {
            const { items, totals } = response;
            const newMap = new Map<string, PostsGroup>();
            // Same reasoning as groupingTimezone above, read straight off this response.
            const responseTimezone = response.timezone ?? timezone;
            const dayOf = (post: Post) =>
                post.day ??
                (post.publishAt ? dayjs(post.publishAt).tz(responseTimezone).format('YYYY-MM-DD') : null);

            totals.forEach((total) => {
                const key =
                    groupBy === PostGroupBy.Stage
                        ? total.stage || 'unknown'
                        : (total.dayKey ??
                          (total.day
                              ? dayjs(total.day).tz(responseTimezone).format('YYYY-MM-DD')
                              : 'unknown'));

                // Find posts for this group
                const groupPosts = items.filter((post) => {
                    if (groupBy === PostGroupBy.Stage) {
                        return post.stage === key;
                    }
                    // Date grouping
                    return dayOf(post) === key;
                });

                newMap.set(key, {
                    key,
                    posts: groupPosts,
                    total: total.value,
                    hasMore: groupPosts.length < total.value,
                    loading: false,
                    expanded: true,
                    toggleExpand: () => toggleGroupExpand(key),
                });
            });

            setPostsGroupedState(newMap);
        },
        [groupBy, timezone, toggleGroupExpand],
    );

    /**
     * Get a post by ID
     */
    const getPost = useCallback(
        (postId: string): Post | null => {
            const post = posts.find((p) => p.id === postId);
            if (post) {
                return post;
            }

            const { items: ideas } = ideasState;
            const idea = (ideas || []).find((p) => p.id === postId);
            if (idea) {
                return idea;
            }

            return null;
        },
        [posts, ideasState],
    );

    /**
     * Sort posts
     */
    /**
     * Earliest first, dateless posts last — matching the server's ASC ordering with NULLS LAST, so an
     * optimistic insert lands where the next refetch will put it.
     */
    const sortPosts = (posts: Post[]) => {
        return posts.sort((a, b) => {
            if (!a.publishAt || !b.publishAt) {
                // Both dateless keeps the existing order; otherwise the dated one comes first
                return Number(!!b.publishAt) - Number(!!a.publishAt);
            }

            return new Date(a.publishAt).getTime() - new Date(b.publishAt).getTime();
        });
    };

    // ---------------------------------------------------------------------------
    // CRUD Operations
    // ---------------------------------------------------------------------------

    const addPost = useCallback(
        (post: Post) => {
            const groupKey = getGroupKey(post);

            setPostsGroupedState((prev) => {
                if (!belongsToGroupedView(post)) {
                    return prev;
                }

                const newMap = new Map(prev);
                const existingGroup = newMap.get(groupKey);

                if (existingGroup) {
                    // Check if post already exists to avoid duplicates
                    const postExists = existingGroup.posts.some((p) => p.id === post.id);
                    if (postExists) {
                        return prev; // No changes needed
                    }

                    // Add post to the group
                    const updatedPosts = sortPosts([post, ...existingGroup.posts]);

                    newMap.set(groupKey, {
                        ...existingGroup,
                        posts: updatedPosts,
                        total: existingGroup.total + 1,
                    });
                } else {
                    // Create new group for this key
                    newMap.set(groupKey, {
                        key: groupKey,
                        posts: [post],
                        total: 1,
                        hasMore: false,
                        loading: false,
                        expanded: true,
                        toggleExpand: () => toggleGroupExpand(groupKey),
                    });
                }

                return newMap;
            });
            if (postIsIdea(post)) {
                setIdeasState((prev) => {
                    return {
                        ...prev,
                        items: [post, ...(prev.items || [])],
                        total: (prev.total || 0) + 1,
                    };
                });
            }
        },
        [getGroupKey, toggleGroupExpand, belongsToGroupedView],
    );

    useEffect(() => {
        setHelper('addPost', addPost);
    }, [addPost, setHelper]);

    const updatePost = useCallback(
        (post: Post) => {
            setPostsGroupedState((prev) => {
                const newMap = new Map(prev);

                const newGroupKey = getGroupKey(post);
                let oldGroupKey: string | null = null;

                // Find the post's current group
                for (const [key, group] of newMap.entries()) {
                    const postIndex = group.posts.findIndex((p) => p.id === post.id);
                    if (postIndex !== -1) {
                        oldGroupKey = key;
                        break;
                    }
                }

                // A post that lost its date leaves the grouped view and shows up in the ideas sidebar
                if (!belongsToGroupedView(post)) {
                    if (oldGroupKey) {
                        const oldGroup = newMap.get(oldGroupKey)!;
                        newMap.set(oldGroupKey, {
                            ...oldGroup,
                            posts: oldGroup.posts.filter((p) => p.id !== post.id),
                            total: Math.max(0, oldGroup.total - 1),
                        });
                    }

                    return newMap;
                }

                // If post not found in any group, just add it
                if (!oldGroupKey) {
                    const targetGroup = newMap.get(newGroupKey);
                    if (targetGroup) {
                        const updatedPosts = sortPosts([...targetGroup.posts, post]);
                        newMap.set(newGroupKey, {
                            ...targetGroup,
                            posts: updatedPosts,
                            total: targetGroup.total + 1,
                        });
                    } else {
                        newMap.set(newGroupKey, {
                            key: newGroupKey,
                            posts: [post],
                            total: 1,
                            hasMore: false,
                            loading: false,
                            expanded: true,
                            toggleExpand: () => toggleGroupExpand(newGroupKey),
                        });
                    }
                    return newMap;
                }

                // Remove from old group
                const oldGroup = newMap.get(oldGroupKey)!;
                const updatedOldPosts = oldGroup.posts.filter((p) => p.id !== post.id);

                if (oldGroupKey === newGroupKey) {
                    // Same group, just update the post
                    const updatedPosts = sortPosts([post, ...updatedOldPosts]);
                    newMap.set(oldGroupKey, {
                        ...oldGroup,
                        posts: updatedPosts,
                    });
                } else {
                    // Different group, move the post
                    newMap.set(oldGroupKey, {
                        ...oldGroup,
                        posts: updatedOldPosts,
                        total: Math.max(0, oldGroup.total - 1),
                    });

                    // Add to new group
                    const newGroup = newMap.get(newGroupKey);
                    if (newGroup) {
                        const updatedPosts = sortPosts([...newGroup.posts, post]);
                        newMap.set(newGroupKey, {
                            ...newGroup,
                            posts: updatedPosts,
                            total: newGroup.total + 1,
                        });
                    } else {
                        newMap.set(newGroupKey, {
                            key: newGroupKey,
                            posts: [post],
                            total: 1,
                            hasMore: false,
                            loading: false,
                            expanded: true,
                            toggleExpand: () => toggleGroupExpand(newGroupKey),
                        });
                    }
                }

                return newMap;
            });

            // Since ideas are fetched separately
            setIdeasState((prev) => {
                const ideaIndex = (prev.items || []).findIndex((p) => p.id === post.id);
                // If post is now an idea, add/update it in ideasState
                if (postIsIdea(post)) {
                    let updatedIdeas: Post[] = [];
                    if (ideaIndex !== -1) {
                        // Update existing idea
                        updatedIdeas = (prev.items || []).slice();
                        updatedIdeas[ideaIndex] = post;
                    } else {
                        // Add new idea
                        updatedIdeas = [post, ...(prev.items || [])];
                    }
                    return {
                        ...prev,
                        items: updatedIdeas,
                        total: ideaIndex === -1 ? (prev.total || 0) + 1 : prev.total,
                    };
                } else {
                    // If post is no longer an idea, remove it from ideasState
                    if (ideaIndex !== -1) {
                        const updatedIdeas = (prev.items || []).filter((p) => p.id !== post.id);
                        return {
                            ...prev,
                            items: updatedIdeas,
                            total: Math.max(0, (prev.total || 0) - 1),
                        };
                    }
                }
                return prev;
            });
        },
        [getGroupKey, toggleGroupExpand, belongsToGroupedView],
    );

    useEffect(() => {
        setHelper('updatePost', updatePost);
    }, [updatePost, setHelper]);

    const navigateToPostRef = useRef(navigateToPost);
    navigateToPostRef.current = navigateToPost;
    useEffect(() => {
        setHelper('navigateToPost', (postId: string) => {
            navigateToPostRef.current({ postId, ignorePostOpenCheck: true });
        });
    }, [setHelper]);

    const navigateBackFromPostRef = useRef(navigateBackFromPost);
    navigateBackFromPostRef.current = navigateBackFromPost;
    useEffect(() => {
        setHelper('navigateBackFromPost', () => {
            navigateBackFromPostRef.current({});
        });
    }, [setHelper]);

    const removePost = useCallback((post: Post) => {
        setPostsGroupedState((prev) => {
            const newMap = new Map(prev);

            // Find and remove post from its group
            for (const [key, group] of newMap.entries()) {
                const postIndex = group.posts.findIndex((p) => p.id === post.id);
                if (postIndex !== -1) {
                    const updatedPosts = group.posts.filter((p) => p.id !== post.id);
                    newMap.set(key, {
                        ...group,
                        posts: updatedPosts,
                        total: Math.max(0, group.total - 1),
                    });
                    break;
                }
            }

            return newMap;
        });

        // Since ideas are fetched separately
        setIdeasState((prev) => ({
            ...prev,
            items: (prev.items || []).filter((p) => p.id !== post.id),
            total: Math.max(0, (prev.total || 0) - 1),
        }));
    }, []);

    const removePostRef = useRef(removePost);
    removePostRef.current = removePost;
    useEffect(() => {
        setHelper('removePost', (postId: string) => {
            removePostRef.current({ id: postId } as Post);
        });
    }, [setHelper]);

    // ---------------------------------------------------------------------------
    // Pagination
    // ---------------------------------------------------------------------------

    const fetchMoreForGroup = useCallback(
        async (groupKey: string) => {
            const currentGroup = postsGroupedState.get(groupKey);
            if (!currentGroup || currentGroup.loading || !currentGroup.hasMore) {
                return;
            }

            // Set loading state
            setPostsGroupedState((prev) => {
                const newMap = new Map(prev);
                const group = newMap.get(groupKey);
                if (group) {
                    newMap.set(groupKey, { ...group, loading: true });
                }
                return newMap;
            });

            try {
                const isDateGroup = groupBy === PostGroupBy.Date;

                const { data } = await fetchMorePosts({
                    variables: {
                        ...(isDateGroup
                            ? {
                                  since: dayjs.tz(groupKey, groupingTimezone).startOf('day').toDate(),
                                  until: dayjs.tz(groupKey, groupingTimezone).endOf('day').toDate(),
                              }
                            : {
                                  stages: [groupKey],
                              }),
                        skip: currentGroup.posts.length,
                        take: LOAD_MORE_POSTS,
                    },
                });

                setPostsGroupedState((prev) => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(groupKey);
                    if (!existing) return prev;

                    // Filter posts for this group
                    let newPosts: Post[] = [];
                    if (isDateGroup) {
                        newPosts = data.posts.items.filter((post) => getGroupKey(post) === groupKey);
                    } else {
                        newPosts = data.posts.items.filter((post) => post.stage === groupKey);
                    }

                    const combinedPosts = [...existing.posts, ...newPosts];
                    const hasMore = combinedPosts.length < existing.total;

                    newMap.set(groupKey, {
                        ...existing,
                        posts: combinedPosts,
                        hasMore,
                        loading: false,
                    });

                    return newMap;
                });
            } catch (error) {
                // Reset loading state on error
                setPostsGroupedState((prev) => {
                    const newMap = new Map(prev);
                    const group = newMap.get(groupKey);
                    if (group) {
                        newMap.set(groupKey, { ...group, loading: false });
                    }
                    return newMap;
                });

                if (error instanceof Error) {
                    enqueueSnackbar(error.message);
                }
            }
        },
        [
            fetchMorePosts,
            postsGroupedState,
            groupBy,
            groupingTimezone,
            getGroupKey,
            enqueueSnackbar,
            LOAD_MORE_POSTS,
        ],
    );

    // ---------------------------------------------------------------------------
    // Context Value
    // ---------------------------------------------------------------------------

    const value: PostsContextValue = {
        // State Posts
        posts,
        postsGrouped: postsGroupedState,
        groupBy,
        totals,
        budgets,
        loadingPosts: loadingPosts || skipPostsQueryNotLoaded,
        errorPosts: errorPosts || null,
        timezone: groupingTimezone,

        // State Ideas
        ideasState: ideasState,
        loadingIdeas,
        errorIdeas: errorIdeas || null,

        // Common operations
        addPost,
        updatePost,
        removePost,
        getPost,

        // Posts operations
        fetchMoreForGroup,

        // Ideas operations
        fetchMoreIdeas,
        refetchIdeas,
    };

    return <PostsContext.Provider value={value}>{children}</PostsContext.Provider>;
};

const PublicPostsProvider = ({ children, viewType }: PropsWithChildren<{ viewType: PostsViewType }>) => {
    const timezone = dayjs.tz.guess();

    return (
        <PostsProviderContent viewType={viewType} timezone={timezone}>
            {children}
        </PostsProviderContent>
    );
};

const PrivatePostsProvider = ({ children, viewType }: PropsWithChildren<{ viewType: PostsViewType }>) => {
    const {
        workspace: { timezone: wTz },
    } = useSession();
    return (
        <PostsProviderContent viewType={viewType} timezone={wTz?.timezone}>
            {children}
        </PostsProviderContent>
    );
};

export const PostsProvider = ({
    children,
    viewType,
    publicView,
}: PropsWithChildren<{ viewType: PostsViewType; publicView?: boolean }>) => {
    if (publicView) {
        return <PublicPostsProvider viewType={viewType}>{children}</PublicPostsProvider>;
    }
    return <PrivatePostsProvider viewType={viewType}>{children}</PrivatePostsProvider>;
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access the posts context.
 * Must be used within a PostsProvider.
 */
export const usePosts = () => {
    const context = useContext(PostsContext);
    if (!context) {
        throw new Error('usePosts must be used within a PostsProvider');
    }
    return context;
};

/**
 * Hook to access the posts context optionally.
 * Returns null if not within a PostsProvider.
 * Useful for components that may be used outside the posts context.
 */
export const usePostsOptional = () => {
    return useContext(PostsContext);
};
